import React, {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Modal} from "components/Modal";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {IconNotificationCircle, IconCopyEvidence} from "components/icons";
import {color} from "constants/color.consts";
import {dateTimeFormat} from "helpers/datetime";
import {formatPersonalNumber} from "helpers/number.formats";
import {signContractIdentifyMethod} from "../constants";

export const SignContractIdentificationDetailsModal = ({participant, methodKind, onClose}) => {
  const {t} = useTranslation();
  const responseObject = useMemo(
    () => participant?.responseObject,
    [participant?.responseObject]
  );

  const hasResponsePayload = useMemo(() => responseObject != null && responseObject !== "", [responseObject]);

  const handleCopyEvidence = useCallback(() => {
    if (!hasResponsePayload) return;
    const identifyMethod = participant?.identifyMethod;
    const payload =
      identifyMethod === signContractIdentifyMethod.SMS
        ? {
            "Identification method": "SMS",
            "Number verified by SMS": participant?.phoneNumber,
            "Time of identification": dateTimeFormat(participant?.authenticatedAt, ", ")
          }
        : {
            "Identification method": "BankID",
            Name: responseObject?.name,
            "Personal number": formatPersonalNumber(responseObject?.number),
            "Time of identification": dateTimeFormat(participant?.authenticatedAt, ", ")
          };
    const text = JSON.stringify(payload, null, 2);
    navigator.clipboard?.writeText(text).catch(() => {});
  }, [participant, responseObject, hasResponsePayload]);

  const isBankId = methodKind === signContractIdentifyMethod.BANKID;
  const methodLabel = isBankId
    ? t("SIGN_CONTRACT_IDENTIFY_METHOD_BANKID")
    : t("SIGN_CONTRACT_IDENTIFY_METHOD_SMS");

  return (
    <Modal
      titleIcon={<IconNotificationCircle color={color.black} addClass="mr-2" />}
      titleText="SIGN_CONTRACT_IDENTIFICATION_DETAILS_TITLE"
      boldTitle
      noCloseButton
      noCloseOnEsc={false}
      noCenterContent
      width="420"
      rounded
      whiteTitle
      onClose={onClose}
    >
      <div className="pb-8 pl-8 pr-8 signing_contract__identification-details">
        <div className="signing_contract__identification-details__row">
          <span className="signing_contract__identification-details__label">
            {t("SIGN_CONTRACT_IDENTIFICATION_DETAILS_METHOD")}
          </span>
          <span className="signing_contract__identification-details__value">{methodLabel}</span>
        </div>
        {isBankId ? (
          <>
            <div className="signing_contract__identification-details__row">
              <span className="signing_contract__identification-details__label">
                {t("SIGN_CONTRACT_IDENTIFICATION_DETAILS_NAME")}
              </span>
              <span className="signing_contract__identification-details__value">
                {responseObject?.name ?? "—"}
              </span>
            </div>
            <div className="signing_contract__identification-details__row">
              <span className="signing_contract__identification-details__label">
                {t("SIGN_CONTRACT_IDENTIFICATION_DETAILS_PERSONAL_NUMBER")}
              </span>
              <span className="signing_contract__identification-details__value">
                {formatPersonalNumber(responseObject?.number)}
              </span>
            </div>
          </>
        ) : (
          <div className="signing_contract__identification-details__row">
            <span className="signing_contract__identification-details__label">
              {t("SIGN_CONTRACT_IDENTIFICATION_DETAILS_SMS_NUMBER_LABEL")}
            </span>
            <span className="signing_contract__identification-details__value">
              {participant.phoneNumber ?? "—"}
            </span>
          </div>
        )}
        <div className="signing_contract__identification-details__row">
          <span className="signing_contract__identification-details__label">
            {t("SIGN_CONTRACT_IDENTIFICATION_DETAILS_TIME")}
          </span>
          <span className="signing_contract__identification-details__value">
            {dateTimeFormat(participant?.authenticatedAt, ", ") || "—"}
          </span>
        </div>
        <div className="df-row-jsb-center mt-8">
          {hasResponsePayload ? (
            <ButtonTransparent
              name={
                isBankId
                  ? "SIGN_CONTRACT_IDENTIFICATION_DETAILS_COPY_BANKID"
                  : "SIGN_CONTRACT_IDENTIFICATION_DETAILS_COPY_SMS"
              }
              icon={<IconCopyEvidence />}
              noBorder
              addClass="df-row-center fz-13"
              color="black"
              onClick={handleCopyEvidence}
            />
          ) : (
            <span />
          )}
          <ButtonTransparent
            name="SIGN_CONTRACT_IDENTIFICATION_DETAILS_CLOSE"
            medium
            nonTransparent
            onClick={onClose}
          />
        </div>
      </div>
    </Modal>
  );
};
